// The only module on the /helen page that talks to Supabase. Everything else
// takes plain rows, which keeps the components testable without a network.
//
// Access control lives in Postgres, not here: RLS restricts every table to the
// two rows in `members`, and withholds the other person's rating until you
// have rated an entry yourself. Nothing in this file is load-bearing for
// privacy — it would be just as safe if a stranger read it.

import { supabase } from '../config/supabase';
import { MAX_UPLOAD_BYTES, extensionOf } from './media';

// Supabase Auth identifies people by email, but we sign in with a username.
export const EMAIL_DOMAIN = 'helen.app';
export const MEDIA_BUCKET = 'helen';
const SIGNED_URL_TTL = 3600;

export const usernameToEmail = (username) =>
  `${String(username || '').trim().toLowerCase()}@${EMAIL_DOMAIN}`;

const randomId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function storagePathFor(entryId, fileName) {
  return `entries/${entryId}/${randomId()}.${extensionOf(fileName) || 'jpg'}`;
}


// Supabase returns { data, error }; the page would rather catch a throw.
const unwrap = ({ data, error }, context) => {
  if (error) {
    const err = new Error(error.message || `Could not ${context}.`);
    err.context = context;
    throw err;
  }
  return data;
};

// ------------------------------------------------------------------ session

export async function signIn(username, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: usernameToEmail(username),
    password,
  });

  if (error) {
    throw new Error(
      /invalid login/i.test(error.message)
        ? "That username and password don't match."
        : error.message
    );
  }
  return data.session;
}

export const signOut = () => supabase.auth.signOut();

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

export function onAuthChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(session));
  return () => data.subscription.unsubscribe();
}

// --------------------------------------------------------------------- data

export async function fetchMembers() {
  return unwrap(
    await supabase.from('members').select('user_id, username, display_name, initial, accent'),
    'load who you are'
  );
}

export async function fetchCategories() {
  return unwrap(
    await supabase.from('categories').select('slug, label, emoji, sort_order').order('sort_order'),
    'load categories'
  );
}

export async function fetchEntries() {
  return unwrap(
    await supabase
      .from('entries')
      .select(
        `id, category, title, subtitle, occurred_on, created_by, created_at, rater_ids,
         ratings ( id, user_id, score, note, updated_at ),
         photos ( id, storage_path, caption, uploaded_by )`
      )
      .order('occurred_on', { ascending: false })
      .order('created_at', { ascending: false }),
    'load your entries'
  );
}

export async function createEntry({ category, title, subtitle, occurredOn, userId }) {
  return unwrap(
    await supabase
      .from('entries')
      .insert({
        category,
        title: title.trim(),
        subtitle: subtitle?.trim() || null,
        occurred_on: occurredOn,
        created_by: userId,
      })
      .select('id, category, title, subtitle, occurred_on, created_by, created_at, rater_ids')
      .single(),
    'save that entry'
  );
}

// One row per person per entry, so re-rating edits your own score in place.
export async function saveRating({ entryId, userId, score, note }) {
  return unwrap(
    await supabase
      .from('ratings')
      .upsert(
        { entry_id: entryId, user_id: userId, score, note: note?.trim() || null },
        { onConflict: 'entry_id,user_id' }
      )
      .select('id, user_id, score, note, updated_at')
      .single(),
    'save your rating'
  );
}

// -------------------------------------------------------------------- media

export async function uploadMedia({ entryId, userId, file }) {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is over 50 MB. Trim the clip, or send it at a smaller size.`
    );
  }

  const path = storagePathFor(entryId, file.name);
  const { error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream' });
  if (error) throw new Error(`Could not upload ${file.name}: ${error.message}`);

  return unwrap(
    await supabase
      .from('photos')
      .insert({ entry_id: entryId, storage_path: path, uploaded_by: userId })
      .select('id, storage_path, caption, uploaded_by')
      .single(),
    'save that file'
  );
}

// The row is deleted before the file: if the storage call then fails, the page
// is still correct and only an unreferenced file is left behind.
export async function deleteMedia(item) {
  unwrap(await supabase.from('photos').delete().eq('id', item.id), 'remove that file');

  const { error } = await supabase.storage.from(MEDIA_BUCKET).remove([item.storage_path]);
  if (error) {
    throw new Error(`Taken off the page, but the file is still in storage: ${error.message}`);
  }
}

// The bucket is private, so every photo and clip needs a short-lived signed
// URL. Returns a { storage_path: url } map for the paths that resolved.
export async function signMediaUrls(paths) {
  if (!paths.length) return {};

  const { data, error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL);
  if (error) return {};

  return (data || []).reduce((map, item) => {
    if (item.signedUrl) map[item.path] = item.signedUrl;
    return map;
  }, {});
}
